import ImsAuth from './ims_auth.js';
import ImsEvents from './ims_events.js';
import { createPostLoginAction, getPostLoginActionParamsForUrl } from './post_login_actions/post_login_creator.js';
import { openSUSILightDialog } from './susi_light_util.js';
import UrlService from './url_service.js';
import cookie from './cookie.js';

const REQUIRE_LOGIN_EXPERIMENT_MAX_NUM_REJECTIONS = 3;

/**
 * If the user is already logged in, this method will resolve a promise
 * immediately without redirecting or creating a post login action.
 *
 * If the user is not already logged in, this method will create a post login
 * action on the server side and then redirect the user to the Adobe login
 * page with the post login action id in the url.
 *
 * @param {String} actionName The name of the action that should be executed after login
 * @param {Object} data An object representing the extra data needed to complete the action
 *
 * @return {Promise}
 */
export function handleAuthenticationWithPostLoginAction(actionName, data) {
  return new Promise((resolve, reject) => {
    ImsEvents.addOnReadyHandler(function() {
      if (ImsAuth.isSignedInUser()) {
        resolve();
      } else {
        // component only shows up if enable_susi_light flipper flag is enabled
        const susiLight = document.querySelector('af-susi-light');
        if (susiLight) {
          createPostLoginAction(actionName, data)
          .then(postLoginActionId => {
            updateSusiLightRedirectUri(postLoginActionId);
            openSUSILightDialog(getPostLoginUrl(postLoginActionId));
          })
          .catch(reject);
        } else {
          createPostLoginAction(actionName, data)
          .then(redirectToLoginPage)
          .catch(reject);
        }
      }
    });
  });
}

export async function handleRequireLoginLinkClick(event) {
  event.preventDefault();
  const path = this.href;
  const postLoginActionId = await createPostLoginAction('redirect');
  const susiLight = document.querySelector('af-susi-light');
  if (!susiLight) {
    return redirectToLoginPage(postLoginActionId, path);
  }
  updateSusiLightRedirectUri(postLoginActionId, this.href);
  openSUSILightDialog(this.href, () => {
    // If the dialog is intentionally closed, redirect to the link.
    window.location = path;
  });
}

export function attachLinksWithPostLoginAction() {
  document.querySelectorAll('a').forEach(a => {
    if (a.href.includes(window.location.origin)) {
      a.addEventListener('click', handleRequireLoginLinkClick);
    }
  });
}

/**
 * Initialize the links with post login action by attaching click event listeners
 * to all anchor elements on the page. This will ensure that any links that require
 * authentication will redirect to the Adobe login page with the appropriate post login action.
 */
export function initLinksWithPostLoginAction() {
  if (!shouldRequireLogin()) {
    return;
  }
  ImsEvents.addOnReadyHandler(() => {
    // If the user is already logged in, we don't need to attach the click handlers
    if (ImsAuth.isSignedInUser()) {
      return;
    }

    if (window.alloy_all) {
      window.alloy_all.data._adobe_corpnew.digitalData.page.pageInfo.sampledExperimentForceLogin = true;
    }

    // Attach click event listeners to all anchor elements on the page post load.
    const observer = new MutationObserver(() => {
      attachLinksWithPostLoginAction();
    });

    observer.observe(document.body, { childList: true, subtree: true });
  });
}

/**
 * Redirect the page to the Adobe login page, and include an encoded version
 * of the original url + the postLoginActionId as the `back_to` param.
 *
 * @param {String} postLoginActionId
 */
export function redirectToLoginPage(postLoginActionId, path = '') {
  const searchParams = new URLSearchParams(UrlService.getSearchParams());
  const postLoginParams = getPostLoginActionParamsForUrl(postLoginActionId);

  Object.entries(postLoginParams).forEach(([key, value]) => {
    searchParams.set(key, value);
  });

  path ||= window.location.pathname;
  window.location = '/login/sso?back_to=' +
    encodeURIComponent(path + '?' + searchParams.toString());
}

/**
 * Get the full redirect URL for the post login action.
 * @param {*} postLoginActionId
 * @returns {String} The full redirect URL for the post login action
 */
function getPostLoginUrl(postLoginActionId, path = '') {
  const currentURL = new URL(window.location.href);
  const origin = currentURL.origin;
  const searchParams = new URLSearchParams(UrlService.getSearchParams());
  const postLoginParams = getPostLoginActionParamsForUrl(postLoginActionId);

  Object.entries(postLoginParams).forEach(([key, value]) => {
    searchParams.set(key, value);
  });

  path ||= window.location.pathname;
  return origin + '/redirect/susi?back_to=' + encodeURIComponent(path + '?' + searchParams.toString());
}

export function shouldRequireLogin() {
  return !!(cookie('require_login_experiment_state') &&
    !(cookie('require_login_experiment_state') || '').includes('logged_out') &&
    cookie('require_login_experiment_show_dialog') === 'true' &&
    (parseInt(cookie('require_login_experiment_num_rejections'), 10) || 0) < REQUIRE_LOGIN_EXPERIMENT_MAX_NUM_REJECTIONS);
}

/**
 * SUSI Light needs the full redirect URL with the post login action, rather than just the pathname.
 * @param {*} postLoginActionId
 * @param {*} susiLight
 */
function updateSusiLightRedirectUri(postLoginActionId, path = '') {
  const susiLight = document.querySelector('af-susi-light');
  const redirectURL = getPostLoginUrl(postLoginActionId, path);
  susiLight.updateRedirectUri(redirectURL);
}
